import { useState, useEffect, useCallback } from "react";
import type { Json } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2, GripVertical, Image, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import MediaPickerDialog from "@/components/media/MediaPickerDialog";
import MediaPreviewButton from "@/components/media/MediaPreviewButton";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

interface ColorGradient {
  from: string;
  to: string;
  direction: string;
}

interface ThemeRow {
  id: string;
  name: string;
  slug: string;
  color_gradient: ColorGradient | null;
  color_mode: string;
  illustration_url: string | null;
  order_index: number;
}

const GRADIENT_DIRECTIONS = [
  { value: "to bottom", label: "↓ Haut vers bas" },
  //  { value: "to top", label: "↑ Bas vers haut" },
  //  { value: "to right", label: "→ Gauche vers droite" },
  //  { value: "to left", label: "← Droite vers gauche" },
  //  { value: "to bottom right", label: "↘ Diagonale bas-droite" },
  //  { value: "to bottom left", label: "↙ Diagonale bas-gauche" },
  //  { value: "to top right", label: "↗ Diagonale haut-droite" },
  //  { value: "to top left", label: "↖ Diagonale haut-gauche" },
];

function parseGradient(g: unknown): ColorGradient {
  const obj = g as Record<string, string> | null;
  return {
    from: obj?.from ?? "#ffffff",
    to: obj?.to ?? "#000000",
    direction: obj?.direction ?? "to bottom",
  };
}

function gradientCSS(g: ColorGradient) {
  return `linear-gradient(${g.direction}, ${g.from}, ${g.to})`;
}

function ThemeIllustrationPicker({ url, onChange }: { url: string; onChange: (v: string) => void }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div className="space-y-2">
      <Label>Illustration</Label>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 justify-start text-xs truncate h-9"
          onClick={() => setPickerOpen(true)}
        >
          <Image className="h-3.5 w-3.5 mr-1 shrink-0" />
          {url ? url.split("/").pop() : "Choisir un média…"}
        </Button>
        {url && (
          <>
            <MediaPreviewButton url={url} />
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-destructive shrink-0"
              onClick={() => onChange("")}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </div>
      <MediaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        currentUrl={url || undefined}
        onSelect={(u) => onChange(u ?? "")}
      />
    </div>
  );
}

  export function ThemeManagement() {
  const [themes, setThemes] = useState<ThemeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [editDialog, setEditDialog] = useState<ThemeRow | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [formName, setFormName] = useState("");
  const [formSlug, setFormSlug] = useState("");
  const [formFrom, setFormFrom] = useState("#ffffff");
  const [formTo, setFormTo] = useState("#000000");
  const [formDirection, setFormDirection] = useState("to bottom");
  const [deleteDialog, setDeleteDialog] = useState<ThemeRow | null>(null);
  const [formColorMode, setFormColorMode] = useState("day");
  const [formIllustrationUrl, setFormIllustrationUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const fetchThemes = useCallback(async () => {
    setLoading(true);
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    const { data, count, error } = await supabase
      .from("theme")
      .select("*", { count: "exact" })
      .order("order_index")
      .range(from, to);
    if (!error && data) {
      setThemes(data.map((t) => ({ ...t, color_gradient: parseGradient(t.color_gradient), color_mode: (t as any).color_mode ?? "day", illustration_url: (t as any).illustration_url ?? null })));
      setTotal(count ?? 0);
    }
    setLoading(false);
  }, [page, pageSize]);

  useEffect(() => {
    fetchThemes();
  }, [fetchThemes]);

  const openCreate = () => {
    setIsNew(true);
    setFormName("");
    setFormSlug("");
    setFormFrom("#F5E7D3");
    setFormTo("#B0D0EF");
    setFormDirection("to bottom");
    setFormColorMode("day");
    setFormIllustrationUrl("");
    setEditDialog({} as ThemeRow);
  };

  const openEdit = (t: ThemeRow) => {
    setIsNew(false);
    setFormName(t.name);
    setFormSlug(t.slug);
    const g = t.color_gradient ?? parseGradient(null);
    setFormFrom(g.from);
    setFormTo(g.to);
    setFormDirection(g.direction);
    setFormColorMode(t.color_mode || "day");
    setFormIllustrationUrl(t.illustration_url || "");
    setEditDialog(t);
  };

  const handleSave = async () => {
    if (!formName.trim() || !formSlug.trim()) {
      toast.error("Nom et slug requis");
      return;
    }
    setSaving(true);
    const gradient = { from: formFrom, to: formTo, direction: formDirection };
    if (isNew) {
      const maxOrder = themes.length > 0 ? Math.max(...themes.map((t) => t.order_index)) + 1 : 0;
      const { error } = await supabase.from("theme").insert({
        name: formName.trim(),
        slug: formSlug.trim(),
        color_gradient: gradient as unknown as Json,
        color_mode: formColorMode,
        illustration_url: formIllustrationUrl.trim() || null,
        order_index: maxOrder,
      } as any);
      if (error) {
        toast.error("Erreur lors de la création");
        setSaving(false);
        return;
      }
      toast.success("Thème créé");
    } else {
      const { error } = await supabase
        .from("theme")
        .update({
          name: formName.trim(),
          slug: formSlug.trim(),
          color_gradient: gradient as unknown as Json,
          color_mode: formColorMode,
          illustration_url: formIllustrationUrl.trim() || null,
        } as any)
        .eq("id", editDialog!.id);
      if (error) {
        toast.error("Erreur lors de la modification");
        setSaving(false);
        return;
      }
      toast.success("Thème mis à jour");
    }
    setSaving(false);
    setEditDialog(null);
    fetchThemes();
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    const { error } = await supabase.from("theme").delete().eq("id", deleteDialog.id);
    if (error) {
      toast.error("Erreur lors de la suppression");
      return;
    }
    toast.success("Thème supprimé");
    setDeleteDialog(null);
    fetchThemes();
  };

  const handleDragStart = (id: string) => setDraggedId(id);
  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDrop = async (targetId: string) => {
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      return;
    }
    const oldIdx = themes.findIndex((t) => t.id === draggedId);
    const newIdx = themes.findIndex((t) => t.id === targetId);
    if (oldIdx < 0 || newIdx < 0) {
      setDraggedId(null);
      return;
    }
    const reordered = [...themes];
    const [moved] = reordered.splice(oldIdx, 1);
    reordered.splice(newIdx, 0, moved);
    setThemes(reordered);
    setDraggedId(null);
    // Persist new order
    await Promise.all(reordered.map((t, i) => supabase.from("theme").update({ order_index: i }).eq("id", t.id)));
    fetchThemes();
  };

  return (
    <Card className="border-border/50">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle style={{ fontFamily: "'DM Sans', sans-serif" }}>Thèmes</CardTitle>
        <Button size="sm" variant="outline" onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" /> Nouveau thème
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-muted-foreground">Chargement…</p>
        ) : themes.length === 0 ? (
          <p className="text-muted-foreground">Aucun thème trouvé.</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Gradient</TableHead>
                  <TableHead>Illustration</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {themes.map((theme) => {
                  const g = theme.color_gradient ?? parseGradient(null);
                  return (
                    <TableRow
                      key={theme.id}
                      draggable
                      onDragStart={() => handleDragStart(theme.id)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(theme.id)}
                      className={draggedId === theme.id ? "opacity-50" : ""}
                    >
                      <TableCell>
                        <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                      </TableCell>
                      <TableCell className="font-medium">{theme.name}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{theme.slug}</TableCell>
                      <TableCell className="text-muted-foreground text-sm capitalize">{theme.color_mode}</TableCell>
                      <TableCell>
                        <div
                          className="h-8 w-24 rounded-md border border-border"
                          style={{ background: gradientCSS(g) }}
                          title={`${g.from} → ${g.to}`}
                        />
                      </TableCell>
                      <TableCell>
                        {theme.illustration_url ? (
                          <div className="h-10 w-10 rounded border border-border overflow-hidden flex items-center justify-center bg-muted/30">
                            {theme.illustration_url.endsWith(".lottie") || theme.illustration_url.endsWith(".json") ? (
                              <DotLottieReact src={theme.illustration_url} loop autoplay style={{ height: 36, width: 36 }} />
                            ) : (
                              <img src={theme.illustration_url} alt="" className="max-h-full max-w-full object-contain" />
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(theme)} title="Modifier">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setDeleteDialog(theme)}
                            title="Supprimer"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Lignes par page :</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    setPageSize(Number(v));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[70px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 20, 50, 100].map((s) => (
                      <SelectItem key={s} value={String(s)}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span>
                  {total} résultat{total > 1 ? "s" : ""}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="outline"
                  className="h-8 w-8"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm px-2">
                  {page} / {totalPages}
                </span>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-8 w-8"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>

      {/* Create / Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(o) => !o && setEditDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{isNew ? "Nouveau thème" : "Modifier le thème"}</DialogTitle>
            <DialogDescription>
              {isNew
                ? "Créez un nouveau thème avec un gradient de couleur."
                : `Modification de « ${editDialog?.name ?? ""} »`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Mon thème" />
              </div>
              <div className="space-y-2">
                <Label>Slug</Label>
                <Input value={formSlug} onChange={(e) => setFormSlug(e.target.value)} placeholder="mon-theme" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mode couleur</Label>
                <Select value={formColorMode} onValueChange={setFormColorMode}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Jour</SelectItem>
                    <SelectItem value="night">Nuit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <ThemeIllustrationPicker
                url={formIllustrationUrl}
                onChange={setFormIllustrationUrl}
              />
            </div>

            <div className="space-y-2">
              <Label>Direction du gradient</Label>
              <Select value={formDirection} onValueChange={setFormDirection}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GRADIENT_DIRECTIONS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Couleur de départ</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formFrom}
                    onChange={(e) => setFormFrom(e.target.value)}
                    className="h-10 w-10 rounded border border-input cursor-pointer"
                  />
                  <Input value={formFrom} onChange={(e) => setFormFrom(e.target.value)} className="font-mono text-sm" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Couleur de fin</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formTo}
                    onChange={(e) => setFormTo(e.target.value)}
                    className="h-10 w-10 rounded border border-input cursor-pointer"
                  />
                  <Input value={formTo} onChange={(e) => setFormTo(e.target.value)} className="font-mono text-sm" />
                </div>
              </div>
            </div>

            {/* Gradient Preview */}
            <div className="space-y-2">
              <Label>Aperçu</Label>
              <div
                className="h-24 w-full rounded-lg border border-border"
                style={{ background: gradientCSS({ from: formFrom, to: formTo, direction: formDirection }) }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Enregistrement…" : isNew ? "Créer" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDialog} onOpenChange={(o) => !o && setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce thème ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le thème « {deleteDialog?.name} » sera définitivement supprimé. Les parcours associés perdront leur thème.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
